"use client"

import { useEffect, useState } from 'react'

// Lightweight install banner using beforeinstallprompt event on Chromium browsers
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    // Only show on Android/Chromium to avoid desktop noise after OAuth flows
    const ua = navigator.userAgent || ''
    const isAndroid = /Android/i.test(ua)
    const isChromium = /Chrome|Chromium/i.test(ua)
    if (!isAndroid || !isChromium) return
    // Do not show if already installed or running standalone
    const isStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches
      || (window.navigator as any).standalone === true
    const dismissed = localStorage.getItem('pwa_install_dismissed') === '1'
    const installedFlag = localStorage.getItem('pwa_installed') === '1'
    if (isStandalone || dismissed || installedFlag) return

    const onBIP = (e: any) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setShow(true)
    }
    const onInstalled = () => {
      localStorage.setItem('pwa_installed', '1')
      setShow(false)
      setDeferredPrompt(null)
    }
    window.addEventListener('beforeinstallprompt', onBIP)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  if (!show || !deferredPrompt) return null

  const onInstall = async () => {
    try {
      await deferredPrompt.prompt()
      await deferredPrompt.userChoice
      // Hide the prompt regardless of outcome
      setShow(false)
      setDeferredPrompt(null)
    } catch (err) {
      console.error('Install prompt error:', err)
      setShow(false)
      setDeferredPrompt(null)
    }
  }

  return (
    <div className="fixed bottom-4 inset-x-0 flex justify-center z-50">
      <div className="bg-card border border-border shadow-lg rounded-full px-4 py-2 flex items-center gap-3">
        <span className="text-sm">Instala PocketWise como app</span>
        <button
          onClick={onInstall}
          className="text-sm font-medium bg-teal-400 hover:bg-teal-500 text-slate-900 px-3 py-1 rounded-full"
        >
          Instalar
        </button>
        <button
          onClick={() => { setShow(false); localStorage.setItem('pwa_install_dismissed', '1') }}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Cerrar
        </button>
      </div>
    </div>
  )
}

// Type for Chrome's beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt: () => Promise<void>
}
